import { Cart, MainProduct } from "../../types"
import { areArraysEqual, getActiveMaterialByMainProductVariantId, getEditModeItems, getInitialActivePendantProductVariantIds, getProductOptionNameIndexByName, isSinglePendantProduct } from "../../utils"

export const getInitialActiveMaterial = (mainProduct: MainProduct) => {
  const material = mainProduct.optionsWithValues
    .find(optionWithValues => optionWithValues.name === "Metal")!
    .values[0]

  return material
}

export const getInitialActiveLength = (mainProduct: MainProduct) => {
  const length = mainProduct.optionsWithValues
    .find(optionWithValues => optionWithValues.name === "Length")!
    .values[0]

  return length
}

export const updateUrlQueryProductVariantIds = (mainProductVariantId: string, pendantVariantIds: string[]) => {
  const url = new URL(window.location.href)
  const urlSearchParams = url.searchParams

  const bset = [mainProductVariantId, ...pendantVariantIds].join('-')
  urlSearchParams.set('bset', bset)

  window.history.replaceState({}, '', url.toString())
}

export const getInitialEditProductVariantIds = (mainProduct: MainProduct, cart: Cart) => {
  const editModeItems  = getEditModeItems(mainProduct, cart)
  
  if (!editModeItems) return null
  
  const { chain, pendants } = editModeItems
  
  const isMainProductSinglePendant = isSinglePendantProduct(mainProduct)
  if (isMainProductSinglePendant) {
    const mainProductVariant = mainProduct.variants.find(variant => +variant.id === +chain.variant_id)

    if (!mainProductVariant) {
      return ({
        chain: String(chain.variant_id),
        pendants: []
      })
    }

    const linkedPendantProductVariant = mainProductVariant.metafields.linkedPendantProductVariant

    if (!linkedPendantProductVariant) {
      throw new Error("Linked pendant product variant not found for main product variant in edit mode")
    }

    return ({
      chain: String(chain.variant_id),
      pendants: [String(linkedPendantProductVariant.id)]
    }) 
  }
  
  return ({
    chain: String(chain.variant_id),
    pendants: pendants.map(item => String(item.variant_id)).reverse()   
  }) 
}

export const getInitialUrlQueryProductVariantIds = (mainProduct: MainProduct) => {
  const urlSearchParams = new URLSearchParams(window.location.search)
  const isMainProductSinglePendant = isSinglePendantProduct(mainProduct)

  const availableMainProductVariantIdFallback = mainProduct.variants.find(variant => variant.available) ?? mainProduct.variants[0]
  if (!availableMainProductVariantIdFallback) {
    throw new Error("No main product variant found")
  }

  const mainProductVariantIdFallback = availableMainProductVariantIdFallback.id
  const pendantProductVariantIdsFallback = getInitialActivePendantProductVariantIds(mainProduct, mainProductVariantIdFallback) 

  const urlQueryProductsVariantIds = urlSearchParams.get('bset')

  if (!urlQueryProductsVariantIds) {
    return {
      chain: mainProductVariantIdFallback,
      pendants: pendantProductVariantIdsFallback
    }
  }

  const [urlQueryMainProductVariantId, ...urlQueryPendantVariantIds] = urlQueryProductsVariantIds.split('-')

  const mainProductVariantId = mainProduct.variants
    .find(variant => variant.id == urlQueryMainProductVariantId && (isMainProductSinglePendant || variant.available))?.id || mainProductVariantIdFallback

  let pendantProductVariantIds = urlQueryPendantVariantIds
    .filter(pendantVariantId => 
      mainProduct.metafields.pendantProducts
        .some(pendantProduct => 
          pendantProduct.variants
            .some(variant => Number(variant.id) === Number(pendantVariantId) && (isMainProductSinglePendant || variant.available))
        )
    )
  
  if (pendantProductVariantIds.length < 1) {
    pendantProductVariantIds = pendantProductVariantIdsFallback
  }

  const activeMaterial = getActiveMaterialByMainProductVariantId(mainProduct, mainProductVariantId)
  pendantProductVariantIds = getCorrespondingPendantProductVariantIdsByMaterial(
    mainProduct,
    pendantProductVariantIds,
    activeMaterial,
    isMainProductSinglePendant
  )

  return {
    chain: mainProductVariantId,
    pendants: pendantProductVariantIds
  }
}

export const getCorrespondingPendantProductVariantIdsByMaterial = (
  mainProduct: MainProduct,
  activePendantProductVariantIds: string[],
  material: string,
  includeUnavailable = false
) => {
  const correspondingPendantProductVariantIdsByMaterial = activePendantProductVariantIds
    .map(activePendantProductVariantId => {
      const pendantProduct = mainProduct.metafields.pendantProducts
        .find(pendantProduct => pendantProduct.variants.
          some(pendantProductVariant => Number(pendantProductVariant.id) === Number(activePendantProductVariantId))
        )
      
      if (!pendantProduct) {
        throw new Error(`Pendant product variant with id ${activePendantProductVariantId} not found`)
      }
      
      const pendantProductVariant = pendantProduct.variants
        .find(pendantProductVariant => (
          Number(pendantProductVariant.id) === Number(activePendantProductVariantId) &&
          (includeUnavailable || pendantProductVariant.available)
        ))

      if (!pendantProductVariant) {
        throw new Error(`Available pendant product variant with id ${activePendantProductVariantId} not found`)
      }
      
      const pendantProductMaterialOptionIndex = getProductOptionNameIndexByName(pendantProduct, "Metal")

      const optionsInCorrespondingPendantProductVariant = [
        ...pendantProductVariant.options.slice(0, pendantProductMaterialOptionIndex),
        material,
        ...pendantProductVariant.options.slice(pendantProductMaterialOptionIndex + 1, pendantProductVariant.options.length)
      ]

      const correspondingPendantProductVariant = pendantProduct.variants
        .find(pendantProductVariant => areArraysEqual(pendantProductVariant.options, optionsInCorrespondingPendantProductVariant))
      
      if (!correspondingPendantProductVariant) {
        throw new Error(`Corresponding pendant product variant for material ${material} not found`)
      }

      return correspondingPendantProductVariant.id
    })

  return correspondingPendantProductVariantIdsByMaterial
}

export const getMainProductVariantIdByMaterialAndLength = (
  mainProduct: MainProduct, 
  material: string, 
  length: string
) => { 
  // @TODO: Create enum for option names and their values IF customer customization will be in the scope of existing types
  // @TODO: Move to utilities, getOptionNamePosition
  const materialOptionIndex = getProductOptionNameIndexByName(mainProduct, "Metal")
  const lengthOptionIndex = getProductOptionNameIndexByName(mainProduct, "Length")

  const variant = mainProduct.variants
    .find(variant => (
      variant.options[materialOptionIndex] === material &&
      variant.options[lengthOptionIndex] === length 
    ))!
    .id
  
  return variant
}
